// Getting the snapshot out of the WebView and into the shared container.
//
// `src/snapshot.js` decides WHAT the snapshot is and is pure. This decides WHEN
// it is written and carries it across the Capacitor bridge to the native plugin
// that owns the App Group (iOS) or the shared SharedPreferences file (Android).
//
// On the web this is a no-op: there is no container, there is no widget, and
// the app behaves exactly as it did.
//
// PRIVACY, RESTATED WHERE THE BYTES MOVE. The only destination in this file is
// `SharedSnapshot.write`, which is a native call into the same app's own
// sandbox on the same device. `home` and `work` go there and nowhere else.
// There is no fetch in this module, and there must never be one.

import { watch, ref } from 'vue';

import { API_BASE, isNative, plugin } from './core/native.js';
import { city, stations, watchedRouteIds, places, rules } from './app-state.js';
import { stationById } from './network.js';
import { networkVersion } from './network-data.js';
import { buildSnapshot, snapshotChanged } from './snapshot.js';

/**
 * The plugin, as PLAIN BOUND METHODS rather than as the proxy.
 *
 * `window.Capacitor.Plugins.X` is a Proxy whose `get` answers ANY property with
 * a native-method wrapper — `then` included. Returning it from an async
 * function, or awaiting it, makes promise resolution call `proxy.then(...)`,
 * which becomes a bridge call iOS rejects on a promise nobody holds: the
 * awaiting code then waits for ever, with no error and no log line. So this is
 * SYNCHRONOUS, and what it hands back has no `then` on it. See
 * `src/core/native.js` for the two other places that learned this.
 */
function sharedSnapshot() {
  if (!isNative) return null;
  const p = plugin('SharedSnapshot');
  if (!p?.write) return null;
  return { write: (...args) => p.write(...args) };
}

/**
 * The station whose board is on screen.
 *
 * The saved default is what the widget falls back to, but somebody who has
 * tapped through to Richmond is, for now, a person who cares about Richmond —
 * and the widget they glance at on the platform should agree with the phone in
 * their hand. Set by the departures screen and by nothing else; cleared back to
 * null when it leaves.
 */
export const stationShowing = ref(null);

/** Tell the snapshot which station the board is on. Null means "the default". */
export function noteStationShown(station) {
  const next = station ? { stopId: Number(station.stopId), name: String(station.name ?? '') } : null;
  const held = stationShowing.value;
  if (held?.stopId === next?.stopId) return;
  stationShowing.value = next;
}

let last = null;
let pending = null;

/**
 * Write it, if it says anything new.
 *
 * Debounced by a second because the things behind it move together: switching
 * city rewrites the saved stations, the watched lines and the places in the
 * same tick, and four crossings of the bridge to land one state is three
 * timeline reloads a widget did not need.
 */
export function writeSnapshot(state) {
  const api = sharedSnapshot();
  if (!api) return null;

  const snapshot = buildSnapshot(state);
  if (!snapshotChanged(snapshot, last)) return last;
  last = snapshot;

  clearTimeout(pending);
  pending = setTimeout(() => {
    // Fire and forget, and swallow: a widget that did not hear about a new
    // default station is a widget showing the old one, which is a disappointment
    // rather than a failure. It must never take the app down with it, and the
    // person doing the tapping has nothing to do about it either way.
    try { api.write({ snapshot }); } catch { /* no container on this build */ }
  }, 1_000);

  return snapshot;
}

/**
 * Keep the shared container in step with the app, for the life of the process.
 *
 * Called once from `src/main.js`. One watcher over one derived object, rather
 * than five watchers over five refs: they move together — a city switch
 * rewrites the stations, the lines and the places in the same tick — and five
 * watchers would be five writes of the same state.
 */
export function startSnapshotWrites(ready = Promise.resolve()) {
  if (!isNative) return () => {};

  // NOT BEFORE THE NETWORK IS IN. home and work are names looked up in the
  // city's bundle, and a snapshot written ahead of it says nobody has either.
  // And `networkVersion` is read below, so a bundle that arrives later (a boot
  // load that failed and recovered) writes the snapshot again.
  let stop = () => {};
  let stopped = false;
  ready.then(() => {
    if (!stopped) stop = begin();
  });
  return () => { stopped = true; stop(); };
}

function begin() {
  const read = () => ({
    network: networkVersion.value,
    city: city.value,
    apiBase: API_BASE,
    station: stationShowing.value ?? stations.value.find((s) => s.isDefault) ?? stations.value[0] ?? null,
    stations: stations.value,
    lines: [...watchedRouteIds.value],
    home: withName(places.value.home),
    work: withName(places.value.work),
    // The rules of the city in force, which `buildSnapshot` cuts down to the
    // two ends and the window. They are read here rather than resolved here:
    // a rule's `path` is the one part the widget has no use for, and the place
    // to drop it is the place that decides what the snapshot IS.
    rules: rules.value,
  });

  return watch(read, (next) => writeSnapshot(next), { deep: true, immediate: true });
}

/**
 * A place, with the station's name attached.
 *
 * `places` holds a stop id and a radius and deliberately nothing else — it is
 * an index into the bundle, not a copy of it. The shared container has no
 * bundle, so the name is resolved here, once, on the way out. A stop id this
 * city's bundle cannot name is not a place, which is the same answer
 * `placeStation` gives it.
 */
function withName(place) {
  if (!place) return null;
  const station = stationById(place.stopId);
  if (!station) return null;
  return { stopId: place.stopId, name: station.name, walkMinutes: place.walkMinutes };
}
